// Trình xem file đính kèm: PDF (iframe), ảnh (zoom/xoay), định dạng khác → tải xuống. File mẫu hiển thị trang giả lập.
import { useEffect, useRef, useState } from 'react';
import { Button, Select, Tooltip, message } from 'antd';
import { Download, Printer, ZoomIn, ZoomOut, RotateCw, Maximize, FileQuestion, Leaf } from 'lucide-react';
import type { DocFile } from '../data';
import { getFile } from '../fileStore';
import { fmtSize } from '../logic';
import { FileTypeIcon } from '../utils';

interface Props {
  files: DocFile[];
  /** File vừa chọn nhưng chưa lưu (màn tạo/sửa) */
  localBlobs?: Record<string, Blob>;
  placeholder?: { title: string; code: string; summary: string; unit: string };
  fileId?: string;
  onFileChange?: (id: string) => void;
  onAccess?: (action: 'download' | 'print', file: DocFile) => void;
  className?: string;
}

const IMG = ['png', 'jpg', 'jpeg'];

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Tải 1 file: lấy nội dung từ IndexedDB; file mẫu không có nội dung thật */
export async function downloadDocFile(file: DocFile, localBlobs?: Record<string, Blob>) {
  const blob = localBlobs?.[file.id] || (file.demo ? undefined : await getFile(file.id));
  if (!blob) {
    message.info(file.demo ? 'Đây là file của dữ liệu mẫu, không có nội dung thật để tải.' : 'Không tìm thấy nội dung file trên trình duyệt này.');
    return false;
  }
  downloadBlob(blob, file.name);
  return true;
}

function printHtml(html: string) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => iframe.remove(), 1000);
  }, 300);
}

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export default function FileViewer({ files, localBlobs, placeholder, fileId, onFileChange, onAccess, className = 'h-[calc(100vh-240px)] min-h-[480px]' }: Props) {
  const [selected, setSelected] = useState<string | undefined>(fileId || files[0]?.id);
  const [state, setState] = useState<{ loading: boolean; url?: string; blob?: Blob }>({ loading: false });
  const [zoom, setZoom] = useState(100);
  const [rotate, setRotate] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (fileId) setSelected(fileId); }, [fileId]);
  useEffect(() => {
    if (!files.some(f => f.id === selected)) setSelected(files[0]?.id);
  }, [files, selected]);

  const file = files.find(f => f.id === selected);

  useEffect(() => {
    let url: string | undefined;
    let cancelled = false;
    setZoom(100);
    setRotate(0);
    if (!file) { setState({ loading: false }); return; }
    const local = localBlobs?.[file.id];
    setState({ loading: true });
    (local ? Promise.resolve(local) : file.demo ? Promise.resolve(undefined) : getFile(file.id)).then(blob => {
      if (cancelled) return;
      if (blob) url = URL.createObjectURL(blob);
      setState({ loading: false, url, blob });
    });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [file, localBlobs]);

  const select = (id: string) => { setSelected(id); onFileChange?.(id); };

  if (!files.length) {
    return (
      <div className={`flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-500 ${className}`}>
        <FileQuestion className="mb-2 h-10 w-10 text-slate-300" />
        Chưa có file đính kèm để xem
      </div>
    );
  }

  const ext = file?.ext || '';
  const isPdf = ext === 'pdf' && !!state.url;
  const isImg = IMG.includes(ext) && !!state.url;
  const showPlaceholder = !state.url && !!file?.demo;
  const canTransform = isImg || showPlaceholder;
  const canPrint = isPdf || isImg || showPlaceholder;

  const download = async () => {
    if (!file) return;
    if (await downloadDocFile(file, localBlobs)) onAccess?.('download', file);
  };

  const print = () => {
    if (!file) return;
    if (isPdf) frameRef.current?.contentWindow?.print();
    else if (isImg) printHtml(`<html><body style="margin:0;text-align:center"><img src="${state.url}" style="max-width:100%" onload="window.focus()"></body></html>`);
    else if (placeholder) printHtml(`<html><body style="font-family:Arial;padding:40px"><h2>${esc(placeholder.title)}</h2><p><b>Mã:</b> ${esc(placeholder.code)}<br><b>Đơn vị ban hành:</b> ${esc(placeholder.unit)}</p><p>${esc(placeholder.summary)}</p><p style="color:#999">(File mẫu — ${esc(file.name)})</p></body></html>`);
    onAccess?.('print', file);
  };

  const fullscreen = () => boxRef.current?.requestFullscreen?.();

  return (
    <div ref={boxRef} className={`flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-sm ${className}`}>
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 bg-[#2c6b7f] px-3 text-white">
        <Select
          size="middle"
          value={selected}
          onChange={select}
          className="w-44 sm:w-72"
          options={files.map(f => ({ value: f.id, label: f.name }))}
        />
        <div className="hidden items-center gap-2 text-sm lg:flex">
          <Tooltip title={canTransform ? 'Thu nhỏ' : 'Dùng thanh công cụ của trình xem PDF'}>
            <button disabled={!canTransform || zoom <= 50} onClick={() => setZoom(z => z - 25)} className="rounded p-1 hover:bg-white/10 disabled:opacity-40"><ZoomOut size={16} /></button>
          </Tooltip>
          <span className="w-12 text-center">{canTransform ? `${zoom}%` : '—'}</span>
          <button disabled={!canTransform || zoom >= 200} onClick={() => setZoom(z => z + 25)} className="rounded p-1 hover:bg-white/10 disabled:opacity-40"><ZoomIn size={16} /></button>
          <div className="h-4 w-px bg-white/30" />
          <Tooltip title="Xoay"><button disabled={!canTransform} onClick={() => setRotate(r => (r + 90) % 360)} className="rounded p-1.5 hover:bg-white/10 disabled:opacity-40"><RotateCw size={16} /></button></Tooltip>
          <Tooltip title="Toàn màn hình"><button onClick={fullscreen} className="rounded p-1.5 hover:bg-white/10"><Maximize size={16} /></button></Tooltip>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Tải xuống"><button onClick={download} className="rounded p-1.5 hover:bg-white/10"><Download size={18} /></button></Tooltip>
          <Tooltip title={canPrint ? 'In tài liệu' : 'Định dạng này không in trực tiếp được, hãy tải xuống'}>
            <button disabled={!canPrint} onClick={print} className="rounded p-1.5 hover:bg-white/10 disabled:opacity-40"><Printer size={18} /></button>
          </Tooltip>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="hidden w-56 shrink-0 flex-col gap-1 overflow-y-auto border-r border-slate-200 bg-slate-50 p-2 md:flex">
          {files.map(f => (
            <button
              key={f.id}
              onClick={() => select(f.id)}
              className={`flex items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors ${f.id === selected ? 'bg-[#ecf6f9] font-semibold text-[#2c6b7f] ring-1 ring-[#3e91ab]/40' : 'text-slate-600 hover:bg-white'}`}
            >
              <FileTypeIcon ext={f.ext} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{f.name}</span>
                <span className="text-[11px] font-normal text-slate-400">{fmtSize(f.size)}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="flex min-w-0 flex-1 justify-center overflow-auto bg-slate-200 p-3 sm:p-6">
          {state.loading ? (
            <div className="self-center text-sm text-slate-500">Đang tải file…</div>
          ) : isPdf ? (
            <iframe ref={frameRef} title={file?.name} src={state.url} className="h-full w-full rounded bg-white" />
          ) : isImg ? (
            <img src={state.url} alt={file?.name} className="max-w-none self-center shadow-lg transition-transform" style={{ transform: `scale(${zoom / 100}) rotate(${rotate}deg)` }} />
          ) : showPlaceholder && placeholder ? (
            <div className="origin-top transition-transform" style={{ transform: `scale(${zoom / 100}) rotate(${rotate}deg)` }}>
              <div className="relative min-h-[760px] w-[640px] max-w-full rounded-sm bg-white p-10 text-slate-700 shadow-lg">
                <div className="mb-8 flex items-center gap-2 text-xl font-bold uppercase tracking-widest text-slate-200"><Leaf size={24} /> KHOTHI</div>
                <div className="text-xs text-slate-400">{placeholder.unit} · {placeholder.code}</div>
                <h2 className="mb-4 mt-1 text-lg font-bold text-slate-800">{placeholder.title}</h2>
                <p className="text-sm leading-relaxed">{placeholder.summary}</p>
                <div className="absolute inset-x-10 bottom-10 rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  File “{file?.name}” thuộc dữ liệu mẫu — đây là trang giả lập nội dung.
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center self-center text-center text-sm text-slate-500">
              {file && <FileTypeIcon ext={file.ext} size={48} />}
              <div className="mt-3 font-medium text-slate-700">{file?.name}</div>
              <div className="mt-1">{state.url ? 'Định dạng này chưa hỗ trợ xem trước trên trình duyệt.' : 'Không tìm thấy nội dung file trên trình duyệt này.'}</div>
              {state.url && <Button className="mt-3" icon={<Download size={14} />} onClick={download}>Tải xuống để xem</Button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
